import { createContext } from 'react';
import { getValidLangOrFallback, getLanguageSubtag } from './utils.js';

/**
 * Tries to resolve the language that should be used for translations:
 * 1. From the document html or body `lang` attribute.
 *    This is prioritized, because the component should use the same language
 *    as the page content is written.
 * 2. From the browser language preference setting.
 *    It is the responsibility of the application using the components to
 *    resolve the users preference and set the content language accordingly.
 *    Therefore this is used as a second option only if the page content
 *    language is not set.
 *
 * If the resolution fails (e.g. in server side environment), the function
 * returns `undefined`. The fallback language is determined separately.
 *
 * @returns language code as `string` or `undefined`
 */
const resolveLanguage = () => {
    var _a;
    // This code may be executed on server side e.g. during Next.js build and
    // `Document` and `Navigator` objects may not be defined.
    const doc = typeof document !== 'undefined' ? document : undefined;
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    if (typeof (doc === null || doc === void 0 ? void 0 : doc.documentElement.lang) === 'string' &&
        doc.documentElement.lang.length > 0) {
        return getLanguageSubtag(doc.documentElement.lang);
    }
    if (typeof ((_a = doc === null || doc === void 0 ? void 0 : doc.body) === null || _a === void 0 ? void 0 : _a.lang) === 'string' && doc.body.lang.length > 0) {
        return getLanguageSubtag(doc.body.lang);
    }
    if (typeof (nav === null || nav === void 0 ? void 0 : nav.language) === 'string' && nav.language.length > 0) {
        return getLanguageSubtag(nav.language);
    }
};
/**
 * Returns the default `LocaleContext` initialized using either resolved
 * language preference or the fallback language.
 *
 * If a application wants to control the translation language, the context
 * should be initialized using:
 * ```
 * <LocaleContext.Provider lang={languageCode}></LocaleContext.Provider>
 * ```
 */
const LocaleContext = createContext({
    lang: getValidLangOrFallback(resolveLanguage()),
});

export { LocaleContext, resolveLanguage };
//# sourceMappingURL=LocaleContext.js.map
